export type LocalStore<T> = {
  key: string;
  set: (value: T | undefined | null) => void;
  get: () => T | null;
  remove: () => void;
};

/**
 * Safely saves a value to localStorage under the specified key.
 *
 * @param key - The localStorage key
 * @param value - The value to store
 */
export const setLocalStorageItem = <T>(key: string, value: T): void => {
  if (typeof window === "undefined") return; // SSR safety

  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
  }
};

/**
 * Retrieves and deserializes a value from localStorage.
 *
 * @param key - The localStorage key
 * @param fallback - Fallback value if nothing is found or parsing fails
 * @returns The parsed value or the fallback
 */
export const getLocalStorageItem = <T>(
  key: string,
  fallback?: T,
): T | undefined => {
  if (typeof window === "undefined") return fallback;

  try {
    const item = localStorage.getItem(key);
    if (!item) return fallback;

    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`Error parsing localStorage key "${key}":`, error);
    return fallback;
  }
};

/**
 * Deletes a localStorage item by key.
 *
 * @param key - The key to remove
 */
export const removeLocalStorageItem = (key: string): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
  }
};

/**
 * Clears all key/value pairs from the browser's localStorage.
 *
 * @remarks
 * This function will remove all data stored in localStorage for the current domain.
 * Use with caution, as this action is irreversible and will affect all users' data
 * stored in localStorage for the application.
 *
 * @example
 * // Clear all localStorage data
 * clearLocalStorage();
 */
export const clearLocalStorage = () => localStorage.clear();

const isBrowser = () =>
  typeof window !== "undefined" && !!window.sessionStorage;

/**
 * Safely saves a value to sessionStorage under the specified key.
 *
 * If value is undefined or null, the key will be removed.
 */
export const setSessionStorageItem = <T>(
  key: string,
  value: T | undefined | null,
): void => {
  if (!isBrowser()) return;

  try {
    if (value === undefined || value === null) {
      window.sessionStorage.removeItem(key);
      return;
    }
    const serialized = JSON.stringify(value);
    window.sessionStorage.setItem(key, serialized);
  } catch (error) {
    // Storage may throw in some environments (private mode / quota exceeded)
    // Keep failure silent but log for debugging
    console.error(`Error setting sessionStorage key "${key}":`, error);
  }
};

/**
 * Retrieves and deserializes a value from sessionStorage.
 *
 * Returns null when the key does not exist or when parsing fails.
 */
export const getSessionStorageItem = <T>(key: string): T | null => {
  if (!isBrowser()) return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (error) {
    // If parsing fails, remove the invalid item and return null
    console.warn(`Error parsing sessionStorage key "${key}":`, error);
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return null;
  }
};

/**
 * Deletes a sessionStorage item by key.
 */
export const removeSessionStorageItem = (key: string): void => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing sessionStorage key "${key}":`, error);
  }
};

/**
 * Clears all sessionStorage for the current origin.
 * Use with caution.
 */
export const clearSessionStorage = (): void => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.clear();
  } catch (error) {
    console.error("Error clearing sessionStorage:", error);
  }
};

/**
 * Factory that returns a small store object for a fixed key.
 *
 * Example:
 *   const authStore = createSessionStore<User>("authUser");
 *   authStore.set(user);           // stores user in sessionStorage
 *   const u = authStore.get();     // reads user or null
 *   authStore.remove();            // removes key
 */
export const createSessionStore = <T>(key: string): LocalStore<T> => ({
  key,
  set: (value: T | undefined | null) => setSessionStorageItem<T>(key, value),
  get: () => getSessionStorageItem<T>(key),
  remove: () => removeSessionStorageItem(key),
});
